// ============================================
// Dynamic Hints (matches Unity HintsController.GenerateHints)
// ============================================

import { events, Events } from '../engine/events.js';
import { storyManager } from '../engine/story.js';
import { L } from '../systems/localization.js';
import { renderJournalTab } from './journal.js';
import { isPat } from '../engine/pgate.js';
import { inventoryManager } from '../systems/inventory.js';

// Comfort-training gear tags (4 lines x 3 tiers). Ownership of any of these
// is the real signal — the old owns_*_tN Ink VARs never existed.
const COMFORT_TRAINING_TAGS = [
    'waist_trainer_starter', 'waist_trainer_advanced', 'waist_trainer_expert',
    'nipple_clamps_light', 'nipple_clamps_padded', 'nipple_clamps_heavy',
    'wrist_cuffs_pink', 'wrist_cuffs_locking', 'wrist_cuffs_heavy',
    'wrist_cuffs_leather', 'ankle_cuffs_leather',
    'heels_kitten', 'heels_stiletto', 'heels_platform_locking',
];

let _hintsThrottle = 0;

// ── Dismiss persistence (C11, 2026-04-20) ─────────────────────────────
// Keyed by hint text — text changes when the hint's state does (e.g.
// "buy the cage" → "show master the cage"), so the dismiss auto-
// invalidates on progress. Stored in localStorage with a 48h TTL so
// stale dismisses don't permanently hide hints after the state that
// would have changed the text never came (e.g. player abandoned a path).
const DISMISS_KEY = 'pno_hint_dismissed_v1';
const DISMISS_TTL_MS = 48 * 60 * 60 * 1000;

function _hashText(s) {
    // FNV-1a 32-bit — stable + compact enough for hint-dismiss keying
    let h = 0x811c9dc5;
    for (let i = 0; i < s.length; i++) {
        h ^= s.charCodeAt(i);
        h = Math.imul(h, 0x01000193);
    }
    return (h >>> 0).toString(36);
}

function _loadDismissed() {
    try {
        const raw = localStorage.getItem(DISMISS_KEY);
        if (!raw) return {};
        const obj = JSON.parse(raw);
        const now = Date.now();
        let changed = false;
        for (const [k, ts] of Object.entries(obj)) {
            if (now - ts > DISMISS_TTL_MS) {
                delete obj[k];
                changed = true;
            }
        }
        if (changed) localStorage.setItem(DISMISS_KEY, JSON.stringify(obj));
        return obj;
    } catch (e) {
        return {};
    }
}

function _dismissHint(text) {
    try {
        const obj = _loadDismissed();
        obj[_hashText(text)] = Date.now();
        localStorage.setItem(DISMISS_KEY, JSON.stringify(obj));
    } catch (e) { /* storage full or denied */ }
}

function _isDismissed(text, dismissed) {
    return !!dismissed[_hashText(text)];
}

// Auto-collapse threshold — once the list grows beyond this many hints,
// fold it into a pill on first render so the stats panel doesn't lose
// its real estate. Player can expand manually; collapsed state is NOT
// persisted (each session starts expanded if the list is short).
const COLLAPSE_THRESHOLD = 5;
let _hintsAutoCollapsedOnce = false;

export function updateHints() {
    const now = Date.now();
    if (now - _hintsThrottle < 500) return;
    _hintsThrottle = now;

    const list = document.getElementById('hints-list');
    const rent = document.getElementById('hints-rent');
    const badge = document.getElementById('hints-count-badge');
    if (!list) return;

    const story = storyManager.currentStory;
    if (!story) return;

    const iv = (v) => { try { return story.variablesState[v] || 0; } catch(e) { return 0; } };
    const ib = (v) => { try { const val = story.variablesState[v]; return val === true || val === 1; } catch(e) { return false; } };

    const day = iv('Day'), fem = iv('Femininity'), sub = iv('Submissiveness'), money = iv('Money');
    const mr = iv('MasterRelationship'), arousal = iv('Arousal');
    const rentAmount = iv('RentAmount') || 250, rentDue = iv('RentDueDay') || 7;

    // Rent countdown
    if (rent) {
        const daysLeft = Math.max(0, rentDue - day);
        const urgent = daysLeft <= 3 && money < rentAmount;
        rent.textContent = L.get('hint.rent_display').replace('{amount}', rentAmount).replace('{days}', daysLeft);
        rent.style.color = urgent ? 'var(--red)' : daysLeft <= 3 ? 'var(--gold)' : 'var(--text-faint)';
    }

    // === Generate hints (ported from Unity HintsController.GenerateHints) ===
    const hints = [];
    const HINT_LOCATIONS = {
        'Master': 'Bar', 'Bar': 'Bar', 'Mall': 'Mall', 'Park': 'Park', 'Beach': 'Beach',
        'Club': 'Club', 'Salon': 'Salon', 'Gym': 'Gym', 'Clinic': 'Clinic', 'Office': 'Office',
        'Spa': 'Spa', 'Home': 'Apartment', 'Main Story': null, 'Girlfriend': null, 'Phone': null,
        'Velvet': 'VelvetLounge', 'Cafe': 'MaidCafe', 'Tattoo': 'TattooStudio',
        'Resort': 'Resort', 'RedLight': 'RedLight', 'University': 'University',
    };
    const h = (text, action, priority, cat) => hints.push({
        text: (action ? '\u25B6 ' : '\u2022 ') + text,
        priority,
        cat: cat || '',
        location: HINT_LOCATIONS[cat] || null,
    });

    // --- Priority 0: Master orders ---
    const oChastity = iv('master_order_chastity'), oPlug = iv('master_order_plug');
    const oLaser = iv('master_order_laser'), oHair = iv('master_order_hair'), oMakeup = iv('master_order_makeup');

    if (oChastity === 1) h(L.get('hint.master.buy_cage'), true, 0, 'Master');
    else if (oChastity === 2 && ib('wearing_chastity')) h(L.get('hint.master.show_cage'), true, 0, 'Master');
    else if (oChastity === 2) h(L.get('hint.master.equip_cage'), true, 0, 'Master');

    if (oPlug === 1) h(L.get('hint.master.buy_plug'), true, 0, 'Master');
    else if (oPlug === 2 && ib('wearing_plug')) h(L.get('hint.master.show_plug'), true, 0, 'Master');
    else if (oPlug === 2) h(L.get('hint.master.equip_plug'), true, 0, 'Master');

    if (oLaser === 1 && iv('laser_sessions') >= 5) h(L.get('hint.master.report_laser'), true, 0, 'Master');
    else if (oLaser === 1) h(L.get('hint.master.visit_laser'), true, 0, 'Master');
    // A wig satisfies the Master's hair order just like real extensions —
    // Bar_Order_Hair_Complete gates on `has_long_hair || wearing_wig`
    // (Bar.ink:1671), and journal.js/character-panel.js both count the wig.
    // Without the wig check here a wig-wearer is told "get hair extensions"
    // forever even though the Bar already accepts their look. (foundby VPS)
    if (oHair === 1 && (ib('has_long_hair') || ib('wearing_wig'))) h(L.get('hint.master.report_hair'), true, 0, 'Master');
    else if (oHair === 1) h(L.get('hint.master.get_hair'), true, 0, 'Master');
    if (oMakeup === 1 && iv('makeup_skill') >= 3) h(L.get('hint.master.report_makeup'), true, 0, 'Master');
    else if (oMakeup === 1) h(L.get('hint.master.learn_makeup'), true, 0, 'Master');

    // Club outfit order
    if (ib('club_job_unlocked') && iv('club_manager_order_outfit') === 1)
        h(L.get('hint.club.outfit_order'), true, 0, 'Club');

    // --- Priority 1: Rent / Saturday / Early game ---
    if (iv('bar_outfit') === 0 && day <= 3) h(L.get('hint.story.first_job'), true, 1, 'Main Story');
    if (day >= 3 && money < 50 && iv('bar_outfit') < 2) h(L.get('hint.story.low_cash'), true, 1, 'Main Story');
    if (day % 7 === 6 && !ib('girlfriend_cucked') && !ib('girlfriend_broken_up')) h(L.get('hint.girlfriend.saturday'), true, 1, 'Girlfriend');

    const daysLeft = rentDue - day;
    if (daysLeft >= 0 && daysLeft <= 2) h(L.get('hint.story.rent_due').replace('{days}', daysLeft).replace('{amount}', rentAmount), true, 1, 'Main Story');
    // v0.2.9: rent escalation — missing rent now bites on every route and the
    // squeeze (permanent +$25/miss + nightly stat drain) escalates each week.
    // Surface it after the first miss so the player knows to catch up.
    if (iv('RentMissedCount') >= 1)
        h(L.get('hint.story.rent_escalation') || 'Rent is overdue. The squeeze gets worse every week you fall behind, catch up to ease it.', true, 1, 'Main Story');

    // Salon urgency
    if (iv('laser_sessions') >= 5 && !ib('body_hairless')) h(L.get('hint.salon.laser_maintenance'), true, 1, 'Salon');
    // hair_permanent (salon transplant) never decays; auto_hair (Vanity T3) resets
    // itself overnight — neither needs the salon-urgency nag (VPS 2026-07-02).
    if (ib('has_long_hair') && !ib('hair_permanent') && !ib('auto_hair') && day - iv('hair_last_touchup_day') >= 5) h(L.get('hint.salon.hair_fading'), true, 1, 'Salon');
    if (iv('nail_days_remaining') === 1) h(L.get('hint.salon.nails_chipping'), true, 1, 'Salon');

    // --- BNWO / University Program (0.3.1 descent rework) ---
    // Whole track opt-in + Patreon; gates mirror the REAL hub/entry gates
    // (hint_gate_mirrors_completion). Deliberately NO hint while the player
    // is staying away (bnwo_doubt_stage 1/2): the walk-away pause is
    // pressure-free by design, a nag would undercut it. The cooloff rest day
    // is carried by the journal/phone tracker, not a hint.
    // FREED 0.3.1 (gate audit 2026-07-23): no isPat() leg — opt-in only.
    const bnwoOn = ib('v030_content_enabled') && ib('pref_bnwo') && iv('masc_path') === 0;
    if (bnwoOn) {
        const bnwoSt = iv('bnwo_stage'), bnwoDoubt = iv('bnwo_doubt_stage');
        if (bnwoSt === 0 && ib('bnwo_seed') && !ib('university_discovered'))
            h(L.get('hint.bnwo.lecture') || 'Sit in on the open lecture series at the university campus, Downtown', true, 2, 'University');
        else if (bnwoSt === 0 && ib('university_discovered') && !ib('bnwo_event_today'))
            h(L.get('hint.bnwo.intake') || 'Your intake session is waiting in the far wing of the campus', true, 2, 'University');
        else if (bnwoSt >= 1 && bnwoSt < 6 && bnwoDoubt !== 1 && bnwoDoubt !== 2
                 && !ib('bnwo_event_today') && iv('Day') >= iv('bnwo_cooloff_until'))
            h(L.get('hint.bnwo.session') || 'The professor expects you, one session per day at the campus', true, 2, 'University');
    }

    // --- Priority 2: Location progression ---
    const photoShoots = iv('photographer_shoots');
    if (ib('photographer_card') && photoShoots === 0) h(L.get('hint.park.find_photographer'), false, 2, 'Park');
    else if (ib('photographer_card') && photoShoots < 5) {
        const nextFem = [0, 15, 25, 35, 45][photoShoots] || 45;
        const type = ['outdoor', 'studio', 'lingerie', 'nude', 'portfolio'][photoShoots] || 'portfolio';
        h(L.get('hint.park.photo_shoot').replace('{type}', type).replace('{fem}', nextFem).replace('{count}', photoShoots), false, 2, 'Park');
    }

    if (fem >= 10 && !ib('visitedGloryHoleOneTime') && arousal >= 15) h(L.get('hint.park.restrooms'), false, 2, 'Park');
    const bullyStage = iv('beach_bully_stage');
    if (bullyStage > 0 && bullyStage < 3) h(L.get('hint.beach.bully_encounter').replace('{stage}', bullyStage), false, 2, 'Beach');
    if (bullyStage === 4) h(L.get('hint.beach.bully_blackmail'), false, 3, 'Beach');

    if (oChastity === 1 || oPlug === 1) h(L.get('hint.master.buy_ordered_items'), false, 2, 'Master');

    const laserS = iv('laser_sessions');
    if (laserS > 0 && laserS < 5) h(L.get('hint.salon.laser_progress').replace('{count}', laserS), false, 2, 'Salon');
    const makeupS = iv('makeup_skill');
    if (makeupS > 0 && makeupS < 3) h(L.get('hint.salon.makeup_progress').replace('{count}', makeupS), false, 2, 'Salon');

    // Maid
    const maidDiscovered = ib('maid_discovered'), maidHired = ib('maid_hired');
    const maidStage = iv('maid_stage'), maidShifts = iv('maid_shifts');
    if (ib('jobfinder_installed') && !maidDiscovered && fem >= 10) h(L.get('hint.maid.job_available'), false, 2, 'Maid');
    else if (maidDiscovered && !maidHired) h(L.get('hint.maid.orientation'), false, 2, 'Maid');
    else if (maidHired && maidStage === 1 && maidShifts < 5) h(L.get('hint.maid.promotion_shifts').replace('{count}', maidShifts), false, 2, 'Maid');
    else if (maidHired && maidStage === 2 && maidShifts < 15) h(L.get('hint.maid.vip_shifts').replace('{count}', maidShifts), false, 2, 'Maid');
    if (maidHired && maidStage >= 2 && iv('maid_client_stage') < 5 && iv('maid_client_relationship') >= 2)
        h(L.get('hint.maid.victor_interest'), false, 2, 'Maid');

    // Club
    const clubAccess = ib('club_access'), clubVip = ib('club_vip_access'), sissyFame = iv('sissy_fame');
    if (clubAccess && !clubVip && sissyFame >= 55) h(L.get('hint.club.almost_vip').replace('{fame}', sissyFame), false, 2, 'Club');
    if (clubAccess && !ib('club_job_unlocked') && fem >= 35 && sissyFame >= 20) h(L.get('hint.club.mama_vee_shifts'), false, 2, 'Club');
    if (iv('club_regular_marco') === 1) h(L.get('hint.club.marco'), false, 3, 'Club');
    if (iv('club_regular_diego') === 1) h(L.get('hint.club.diego'), false, 3, 'Club');
    if (iv('club_regular_lily') === 1) h(L.get('hint.club.lily'), false, 3, 'Club');

    // Girlfriend tension (Sarah Rework v0.2.4)
    const sarahTen = iv('sarah_tension');
    const sarahDisc = ib('sarah_discovery_done');
    const sarahProc = ib('sarah_processing');
    const sarahProcStage = iv('sarah_processing_sms_stage');
    const sarahArcChosen = ib('sarah_arc_chosen');
    if (sarahProc && sarahProcStage >= 3)
        h(L.get('hint.girlfriend.coffee_meeting') || 'She wants to meet at the coffee shop on Main this Saturday', false, 1, 'Girlfriend');
    else if (sarahTen >= 6 && !sarahDisc && !ib('girlfriend_broken_up'))
        h((L.get('hint.girlfriend.suspicious') || 'Girlfriend will notice something at her next visit (tension {level}/10)').replace('{level}', sarahTen), false, 2, 'Girlfriend');
    else if (sarahDisc && !sarahArcChosen && !sarahProc)
        h(L.get('hint.girlfriend.awaiting') || 'She needs space. Wait for her to text you', false, 2, 'Girlfriend');

    // Endgame
    const dreams = (ib('endgame_dream_1') ? 1 : 0) + (ib('endgame_dream_2') ? 1 : 0) + (ib('endgame_dream_3') ? 1 : 0);
    if (dreams > 0 && dreams < 3 && !ib('endgame_triggered')) h(L.get('hint.story.endgame_dreams').replace('{count}', dreams), false, 2, 'Main Story');
    else if (fem >= 60 && dreams === 0) h(L.get('hint.story.push_fem_endgame'), false, 3, 'Main Story');

    // --- Priority 3: Discovery ---
    if (iv('sugar_arrangement') >= 1 && iv('sugar_dates') < 5) h(L.get('hint.sugar.next_date'), false, 2, 'Sugar Daddy');
    if (iv('Knowledge_streaming') >= 1) h(L.get('hint.streaming.go_live'), false, 3, 'Streaming');
    else if (fem >= 15) h(L.get('hint.streaming.browse'), false, 3, 'Streaming');
    if (!ib('sissysocial_installed') && fem >= 15) h(L.get('hint.phone.download_sissysocial'), false, 3, 'Phone');
    if (!ib('onlyfems_installed') && ib('sissysocial_installed') && fem >= 25) h(L.get('hint.streaming.download_onlyfems'), false, 3, 'Streaming');
    if (!ib('grindr_installed') && fem >= 15) h(L.get('hint.phone.download_dating'), false, 3, 'Phone');
    if (ib('grindr_installed') && fem >= 10) h(L.get('hint.phone.swipe_grindr'), false, 3, 'Phone');
    if (!ib('taking_hormones') && fem >= 40) h(L.get('hint.story.hormones'), false, 3, 'Main Story');
    // Hint requires: fem >= 50 (matches items.json hypno_usb requiredThreshold so the
    // player can actually buy it the moment they see the hint) AND prior exposure to
    // hypno content via the computer browser (hypno_exposure >= 5 ≈ a couple of
    // sessions on the hypno website). Pre-fix the hint fired at fem 40 with no prior
    // narrative introduction → players hit "go buy a USB at the adult store" before
    // they had ever heard of hypno content. Foundby:VPS player 2026-05-06.
    if (!ib('owns_hypno_usb') && fem >= 50 && sub >= 30 && iv('hypno_exposure') >= 5) h(L.get('hint.story.hypno_usb'), false, 3, 'Main Story');
    if (!ib('gym_member') && day >= 3) h(L.get('hint.gym.membership'), false, 3, 'Gym');
    // v0.2.9 (free since 0.3): Tyler gym body-coach arc. Discovery signal —
    // the GymHub "Ask Tyler about body coaching" choice opens at Fem 15 but has
    // no other prompt. Distinct arc from his GrindrR dating (hint.tyler.date).
    if (fem >= 15 && !ib('tyler_coach_unlocked'))
        h(L.get('hint.tyler.coach') || 'Tyler at the gym has started measuring bodies. Ask him about coaching.', false, 3, 'Gym');
    if (iv('upgrade_bed') + iv('upgrade_desk') + iv('upgrade_vanity') + iv('upgrade_decor') === 0 && money >= 300 && day >= 7)
        h(L.get('hint.home.upgrades'), false, 3, 'Home');
    // Wig counts as long hair here too — don't nag a wig-wearer to buy
    // extensions when the character panel already shows a "Wig" badge.
    if (fem >= 15 && !ib('has_long_hair') && !ib('wearing_wig')) h(L.get('hint.salon.hair_extensions'), false, 4, 'Salon');
    if (!ib('has_belly_piercing') && fem >= 50 && ib('has_hoop_earrings')) h(L.get('hint.salon.belly_piercing'), false, 3, 'Salon');

    // Fem tier gates
    if (fem >= 15 && fem < 25 && !clubAccess) h(L.get('hint.story.unlock_club'), false, 2, 'Main Story');
    if (fem >= 45 && fem < 50) h(L.get('hint.story.items_fem50'), false, 3, 'Main Story');

    // DareMe App
    const daremeInstalled = ib('dareme_installed');
    const dareActive = iv('dare_active');
    const daresCompleted = iv('dares_completed');
    if (!daremeInstalled && fem >= 20) h(L.get('hint.dareme.install'), false, 2, 'Phone');
    if (daremeInstalled && dareActive > 0) {
        const dareLocations = { 1:'Bar', 5:'Bar', 8:'Bar', 3:'Club', 6:'Club', 10:'Club', 2:'Park', 9:'Park', 4:'Beach', 7:'Beach', 11:'Beach' };
        const loc = dareLocations[dareActive] || '?';
        h(L.get('hint.dareme.active').replace('{loc}', loc), true, 1, loc);
    }
    if (daremeInstalled && dareActive === 0 && daresCompleted < 11) h(L.get('hint.dareme.pick'), false, 3, 'Phone');
    if (daremeInstalled && daresCompleted >= 4 && !ib('dare_done_5') && fem >= 35) h(L.get('hint.dareme.tier2'), false, 3, 'Phone');

    // HypnoMind (0.2.9, free since 0.3)
    if (ib('hypno_app_installed')) {
        if (iv('hypnomind_stage') < 1) h(L.get('hint.hypnomind.start'), true, 2, 'Phone');
        else if (iv('hypnomind_depth') < 60) h(L.get('hint.hypnomind.continue'), false, 3, 'Phone');
        // Stage >= 2: the out-of-app public events are armed. Push the player OUT
        // into the city — the other two hints only point back into the app.
        if (iv('hypnomind_stage') >= 2) h(L.get('hint.hypnomind.public'), false, 3, 'Phone');
    }

    // Spa
    const spaDisc = ib('spa_discovered'), spaMember = ib('spa_member');
    const masseurStage = iv('masseur_stage'), camilleStage = iv('camille_stage');
    if (!spaDisc && fem >= 25 && day >= 7) h(L.get('hint.spa.discover'), false, 3, 'Spa');
    else if (spaDisc && !spaMember) h(L.get('hint.spa.membership'), false, 2, 'Spa');
    if (spaMember && masseurStage === 0 && iv('spa_visits') >= 2) h(L.get('hint.spa.dominic'), false, 3, 'Spa');
    else if (masseurStage > 0 && masseurStage < 4) h(L.get('hint.spa.dominic_return'), false, 3, 'Spa');
    if (spaMember && camilleStage > 0 && camilleStage < 3) h(L.get('hint.spa.camille'), false, 3, 'Spa');
    if (spaMember && fem >= 50) {
        const lipT = iv('spa_lip_treatment'), hipT = iv('spa_hip_treatment'), breastT = iv('spa_breast_treatment');
        if (lipT + hipT + breastT === 0) h(L.get('hint.spa.body_mods'), false, 3, 'Spa');
    }

    // Bar Outfit
    const barOutfit = iv('bar_outfit');
    if (barOutfit === 1 && iv('bar_shifts_worked') >= 3) h(L.get('hint.bar.polo_available'), false, 3, 'Bar');
    if (barOutfit === 2 && fem >= 40 && iv('bar_shifts_worked') >= 8) h(L.get('hint.bar.dress_available'), false, 3, 'Bar');

    // Patrick (GrindrR)
    if (ib('matched_patrick') && iv('dating_patrick') < 3) h(L.get('hint.patrick.date'), false, 3, 'Phone');

    // Vincent (GrindrR)
    if (ib('matched_vincent') && iv('dating_vincent') < 3) h(L.get('hint.vincent.date'), false, 3, 'Phone');

    // Hank (GrindrR, 0.2.9 Patreon muse). Post-match nudge, mirrors patrick/vincent.
    if (isPat() && ib('matched_hank') && iv('dating_hank') < 3) h(L.get('hint.hank.date') || 'Hank is hoping to see you again. Check GrindrR.', false, 3, 'Phone');

    // Tyrone (GrindrR, 0.3.1 custom NPC, Patreon). Post-match nudge, mirrors
    // hank; tyrone_enabled mirror so a kill-switch panic-off can't leave a
    // hint pointing at removed content.
    if (isPat() && ib('tyrone_enabled') && ib('matched_tyrone') && iv('dating_tyrone') < 3) h(L.get('hint.tyrone.date') || 'Tyrone wants a rematch. Check GrindrR.', false, 3, 'Phone');

    // Clinic extras
    if (ib('clinic_registered') && iv('doctor_stage') >= 3 && iv('prostate_session_cycle') === 0)
        h(L.get('hint.clinic.prostate'), false, 3, 'Clinic');

    // --- Tyler / Kai / Owen (GrindrR baseline dates) ---
    if (ib('matched_tyler') && iv('dating_tyler') < 3) h(L.get('hint.tyler.date'), false, 3, 'Phone');
    if (ib('matched_kai') && iv('dating_kai') < 3) h(L.get('hint.kai.date'), false, 3, 'Phone');
    if (ib('matched_owen') && iv('dating_owen') < 3) h(L.get('hint.owen.date'), false, 3, 'Phone');

    // --- Velvet Lounge (Brothel, v0.2.4) ---
    const velvetStage = iv('velvet_stage');
    const velvetShifts = iv('velvet_shifts');
    const vivienneRel = iv('vivienne_relationship');
    if (fem >= 30 && velvetStage === 0 && day >= 10) h(L.get('hint.velvet.discover'), false, 3, 'Velvet');
    else if (velvetStage === 1 && fem >= 35) h(L.get('hint.velvet.regular'), false, 2, 'Velvet');
    else if (velvetStage === 2 && fem >= 45) h(L.get('hint.velvet.worker'), false, 2, 'Velvet');
    else if (velvetStage === 3 && velvetShifts < 5) h(L.get('hint.velvet.shifts').replace('{count}', velvetShifts), false, 3, 'Velvet');
    if (velvetStage >= 2 && vivienneRel > 0 && vivienneRel < 5) h(L.get('hint.velvet.vivienne').replace('{stage}', vivienneRel), false, 3, 'Velvet');

    // --- Maid Cafe (Hana, v0.2.3) ---
    const cafeStage = iv('cafe_stage');
    const cafeShifts = iv('cafe_shifts');
    const hanaRel = iv('hana_relationship');
    if (fem >= 20 && cafeStage === 0 && day >= 5) h(L.get('hint.cafe.discover'), false, 3, 'Cafe');
    else if (cafeStage === 1) h(L.get('hint.cafe.talk_hana'), false, 2, 'Cafe');
    else if (cafeStage === 2) h(L.get('hint.cafe.start_work'), false, 2, 'Cafe');
    else if (cafeStage === 3 && cafeShifts < 5) h(L.get('hint.cafe.shifts').replace('{count}', cafeShifts), false, 3, 'Cafe');
    if (cafeStage >= 3 && hanaRel > 0 && hanaRel < 4) h(L.get('hint.cafe.hana').replace('{stage}', hanaRel), false, 3, 'Cafe');

    // --- Office Job (Hartwell) ---
    const officeStage = iv('office_stage');
    const officeBoss = iv('office_boss_stage');
    if (ib('office_discovered') && !ib('office_hired')) h(L.get('hint.office.interview'), false, 2, 'Office');
    else if (ib('office_hired') && officeStage === 1 && iv('office_shifts') < 5)
        h(L.get('hint.office.front_desk'), false, 3, 'Office');
    else if (officeStage >= 2 && officeStage < 4)
        h(L.get('hint.office.promotion').replace('{stage}', officeStage), false, 3, 'Office');
    if (ib('office_hired') && officeBoss > 0 && officeBoss < 5)
        h(L.get('hint.office.boss').replace('{stage}', officeBoss), false, 3, 'Office');

    // --- Tattoo Studio (Jade, v0.2.3) ---
    const jadeVisits = iv('jade_visits');
    const jadeRap = iv('jade_rapport');
    if (!ib('tattoo_discovered') && fem >= 35 && sub >= 20)
        h(L.get('hint.tattoo.discover'), false, 3, 'Tattoo');
    else if (ib('tattoo_discovered') && jadeVisits === 0)
        h(L.get('hint.tattoo.first_visit'), false, 3, 'Tattoo');
    else if (jadeVisits > 0 && jadeRap < 5)
        h(L.get('hint.tattoo.next_session').replace('{rapport}', jadeRap), false, 3, 'Tattoo');
    // Intimate piercings back room (0.3, Patreon). Mirrors the TattooHub c5 gate
    // (jade_rapport >= 3 && Sub >= 30 && is_patreon) — hint mirrors the REAL gate
    // (feedback_hint_gate_mirrors_completion). Quiet once the intro was seen.
    if (isPat() && ib('tattoo_discovered') && jadeRap >= 3 && sub >= 30 && !ib('backroom_intro_seen')
        && !ib('owns_piercing_nipple') && !ib('owns_piercing_cage') && !ib('owns_piercing_thigh'))
        h(L.get('hint.tattoo.backroom'), false, 3, 'Tattoo');

    // --- The Gilded Cage cabaret (0.3, Patreon venue) ---
    if (isPat()) {
        const cabStage = iv('cabaret_stage'), exhibFem = iv('exhib_fem');
        if (!ib('cabaret_discovered') && fem >= 15 && day >= 5)
            h(L.get('hint.cabaret.discover'), false, 3, 'RedLight');
        // Audition-ready mirror: Maxime's offer opens at exhib_fem >= 35.
        else if (ib('cabaret_discovered') && cabStage < 2 && exhibFem >= 35)
            h(L.get('hint.cabaret.audition'), false, 3, 'RedLight');
    }

    // --- Resort (v0.2.5, Dante + Iris) ---
    const resortDisc = ib('resort_discovered');
    const resortVisits = iv('resort_visits');
    const danteStage = iv('dante_stage');
    const irisStage = iv('iris_stage');
    if (!resortDisc && fem >= 30 && money >= 200) h(L.get('hint.resort.discover'), false, 3, 'Resort');
    else if (resortDisc && resortVisits === 0) h(L.get('hint.resort.first_trip'), false, 2, 'Resort');
    else if (resortDisc && !ib('resort_on_site') && day - iv('resort_last_visit_day') >= 7)
        h(L.get('hint.resort.return'), false, 3, 'Resort');
    if (ib('resort_on_site') && danteStage > 0 && danteStage < 5)
        h(L.get('hint.resort.dante').replace('{stage}', danteStage), false, 2, 'Resort');
    if (ib('resort_on_site') && irisStage > 0 && irisStage < 3)
        h(L.get('hint.resort.iris').replace('{stage}', irisStage), false, 3, 'Resort');

    // --- Weekend Events (v0.2.5) ---
    const weekendPending = iv('weekend_event_pending');
    if (weekendPending > 0 && !ib('weekend_event_done_today'))
        h(L.get('hint.weekend.pending'), true, 1, 'Phone');

    // --- Arousal escape valve (Cold Shower / Cold Plunge) ---
    if (arousal >= 50 && !ib('cold_shower_today'))
        h(L.get('hint.arousal.cold_shower'), false, 2, 'Home');
    if (arousal >= 60 && ib('spa_member'))
        h(L.get('hint.arousal.cold_plunge'), false, 2, 'Spa');

    // --- Hypno usage (post-acquire) ---
    if (ib('owns_hypno_usb') && iv('hypno_exposure') < 10)
        h(L.get('hint.story.hypno_listen').replace('{exp}', iv('hypno_exposure')), false, 3, 'Home');

    // --- Cage tier progression (Owner route, downsize chain) ---
    // tier 1 = regular cage (downsize chain start). tier 0 = no cage OR the
    // chastity belt, which is NOT part of the cage-only downsize chain
    // (deep-review L-14: the === 0 gate was pre-belt dead code and started
    // firing a dead hint for belt wearers).
    if (ib('wearing_chastity') && iv('cage_tier') === 1 && mr >= 5)
        h(L.get('hint.master.cage_downsize'), false, 3, 'Master');

    // Master post-orders — vulnerable scene triggers at home, not at the Bar
    if (ib('master_reward_given') && mr >= 7 && !ib('master_opened_up'))
        h(L.get('hint.master.personal'), false, 3, 'Home');

    // --- v0.2.6 → free in v0.2.7: Chastity-Deep / Kennel discovery ---
    // Surfaced once cage is acquired + Sub gate met. Was Patreon-gated in
    // v0.2.6, gate removed in v0.2.7; all players see this hint now.
    // Sub >= 40 matches the actual FetishStoreEntry gate (was 35 — hint fired
    // 5 Sub before the door opens, players walked to a locked door).
    // pref_pet_play (0.3.1 retrofit): hint gate mirrors the real FetishStore
    // entry gate (feedback_hint_gate_mirrors_completion) — no kennel nag when
    // the track is toggled off post-arm. Default-true grandfather keeps old
    // saves unchanged; while v031 is dormant the pref never bites.
    const petPlayOn = !ib('v031_content_enabled') || ib('pref_pet_play');
    if (petPlayOn && ib('wearing_chastity') && sub >= 40 && !ib('mira_introduced'))
        h(L.get('hint.kennel.discover') || 'Ask about the door behind the curtain at Kitten & Chrome', false, 3, 'Velvet');
    else if (petPlayOn && ib('mira_introduced') && iv('mira_relationship') < 3)
        h(L.get('hint.kennel.first_session') || 'Book your first session at the Kennel', false, 3, 'Velvet');

    // --- 0.3.1 Kennel expansion next-step hints (KENNEL_EXPANSION.md) ---
    // Armed + Patreon + pref only; each mirrors its real Kennel_Entry choice
    // gate (feedback_hint_gate_mirrors_completion — never nag at a locked door).
    if (petPlayOn && ib('v031_content_enabled') && isPat() && ib('mira_introduced')) {
        const kenShowReady = (ib('kitten_stage_3_done') || ib('puppy_stage_3_done'))
            && iv('mira_pet_devotion') >= 4 && sub >= 75;
        if (kenShowReady && !ib('kitten_stage_4_done') && !ib('puppy_stage_4_done'))
            h(L.get('hint.kennel.stage4') || 'Mira holds an invitation, the shows are invite-only. Ask at the Kennel', false, 3, 'Velvet');
        else if (ib('mira_collar_owned') && !ib('kennel_overnight_done'))
            h(L.get('hint.kennel.overnight') || 'The Kennel alcove is yours now, come in the evening and stay the night', false, 3, 'Velvet');
        else if (ib('mira_collar_owned') && !ib('playroom_pet_done'))
            h(L.get('hint.kennel.playroom') || "Wear Mira's collar in your own playroom", false, 3, 'Home');
    }

    // --- v0.2.6: Permanent-Lock approach warning ---
    // Long-term cage tracker: at Day 25+ of wearing, a permanent offer becomes
    // possible. Surface as a warning so the player knows the milestone is
    // coming and can prepare (or noun-out).
    // Track chastity_total_days_locked (the actual Sleep.ink milestone driver,
    // resets on relief), NOT chastity_days_worn (a wear-streak counter). Milestone
    // is 40 cumulative locked days (was 60, lowered 0.2.8.1.3). Cap below 40 so the
    // run-up hint stops once the offer is live (SMS + apartment choice + Inkwell
    // walk-in carry it from there).
    const cdw = iv('chastity_total_days_locked');
    if (cdw >= 25 && cdw < 40 && !ib('permanent_lock') && mr >= 6 && !ib('Permanent_Lock_Offer_seen'))
        h((L.get('hint.chastity.permanent_approach') || 'Chastity {days}/40 days, a permanent offer is near').replace('{days}', cdw), false, 2, 'Master');

    // --- v0.2.8: locksmith escape (anti-softlock when keyholder-locked) ---
    // A keyholder or permanent lock means the cage can't be self-unequipped.
    // Surface the back-alley locksmith so a locked player never feels stuck.
    // Mirror isCageKeyholderLocked(): keyholder_npc defaults to "master"
    // (never ''), so the old `!== ''` test was always true and the hint fired
    // for every self-locked cage wearer (deep-review P1-1).
    const keyholderNpc = (() => { try { return story.variablesState['keyholder_npc'] || ''; } catch(e) { return ''; } })();
    const cageHeldByOther = ib('permanent_lock')
        || iv('master_order_chastity') >= 3
        || iv('master_intensity') >= 1
        || (keyholderNpc !== '' && keyholderNpc !== 'master');
    if (ib('wearing_chastity') && cageHeldByOther)
        h(L.get('hint.chastity.locksmith') || 'Locked and want out? A back-alley locksmith in the Red Light District cuts cages off, for a price.', true, 3, 'RedLight');

    // --- v0.2.6 → free in v0.2.7: Doxxing arc (Streaming Rework) ---
    const doxx = iv('doxxing_arc_stage');
    if (doxx === 1) h(L.get('hint.doxxing.hint') || 'A viewer hinted they know your real name, check stream DMs', false, 2, 'Streaming');
    else if (doxx === 2) h(L.get('hint.doxxing.threat') || 'A threat dropped in chat. Resolve it before it goes public.', true, 1, 'Streaming');
    else if (doxx === 3) h(L.get('hint.doxxing.pending') || 'Pick a resolution path: pay, public-reveal, police, or Master defends', true, 1, 'Streaming');

    // --- v0.2.6: Post-ending mode ---
    // Once an ending has fired, the pause-menu "Endgame Progress" entry
    // becomes "Run Summary" (handled in main.js). Help the player find it
    // again if they continue playing post-ending.
    if (ib('ending_reached') || iv('ending_reached'))
        h(L.get('hint.endscreen.run_summary') || 'Pause menu → Run Summary to re-open your ending scene', false, 4, 'Phone');

    // --- v0.2.7: Basement Workshop discovery ---
    // $800 lease unlocks at Day 15 via Apartment Upgrades. Free for everyone
    // since 0.2.8 (owner decision 2026-06-10).
    // day + money already declared at line 87 of this function.
    if (day >= 15 && money >= 800 && !ib('apartment_has_basement'))
        h(L.get('hint.basement.discover') || 'Lease the basement at Apartment Upgrades ($800)', false, 3, 'Home');

    // Workshop NPC invites (Master free; Vincent/Eloïse arcs free since 0.2.8,
    // but both NPCs are forever-Patreon dating NPCs — dating_* >= 2 carries it)
    const mIntensity = iv('master_intensity');
    if (ib('apartment_has_basement') && mIntensity >= 1 && !ib('workshop_master_today') && iv('workshop_master_stage') < 3)
        h(L.get('hint.workshop.invite_master') || `Invite {MasterName} down to the basement`, false, 3, 'Master');
    if (ib('apartment_has_basement') && iv('dating_vincent') >= 2 && !ib('workshop_vincent_today') && iv('workshop_vincent_stage') < 3)
        h(L.get('hint.workshop.invite_vincent') || 'Invite Vincent over to the basement workshop', false, 3, 'Home');

    // --- v0.2.7: Apartment Nest tier progression ---
    // Decor purchases drive nest_tier 0→3. Playroom upgrade gates on tier 2.
    // Surface a hint when player owns the basement but no decor yet, OR
    // when nest_tier is 1 (one decor item) so they understand the gate.
    const nestTier = iv('apartment_nest_tier');
    if (ib('apartment_has_basement') && nestTier < 2 && !ib('apartment_has_playroom'))
        h(L.get('hint.playroom.nest_unlock') || 'Buy Decor items in Apartment Upgrades to unlock the Playroom', false, 3, 'Home');
    if (nestTier >= 2 && money >= 1200 && !ib('apartment_has_playroom'))
        h(L.get('hint.playroom.discover') || 'Convert the Spare Room into a Playroom at Apartment Upgrades ($1200)', false, 3, 'Home');

    // Playroom NPC invites (GF / Hana / Kai — all free since 0.2.8)
    const gfActive = !ib('girlfriend_disabled') && !ib('girlfriend_broken_up') && iv('sarah_arc') !== 5;
    if (ib('apartment_has_playroom') && gfActive && !ib('playroom_gf_today') && iv('playroom_gf_stage') < 3)
        h(L.get('hint.playroom.invite_gf') || 'Invite your girlfriend to the playroom', false, 3, 'Home');
    if (ib('apartment_has_playroom') && iv('dating_kai') >= 2 && !ib('playroom_kai_today') && iv('playroom_kai_stage') < 3)
        h(L.get('hint.playroom.invite_kai') || 'Invite Kai to the playroom', false, 3, 'Home');

    // --- v0.2.7: Comfort Training system ---
    // Hint fires when player owns ANY training-gear item but hasn't started training.
    // Free content, no Patreon gate.
    const ownsTrainingGear = COMFORT_TRAINING_TAGS.some(t => inventoryManager.ownsItem(t));
    const masteryDone = iv('comfort_mastery_unlocked');
    if (ownsTrainingGear && !masteryDone && !ib('comfort_training_today'))
        h(L.get('hint.comfort_training.train') || 'Train tolerance in your apartment to make the gear comfortable', false, 3, 'Home');
    // v0.2.9: latex/restraint acclimation line — separate shared track, surfaced
    // while wearing any restraint-tagged gear that hasn't fully acclimated.
    if (ib('EquippedRestraint') && iv('restraint_tolerance') < 2)
        h(L.get('hint.comfort_training.restraint') || 'New latex and restraints chafe at first. Acclimate to them in your apartment to ease the comfort hit.', false, 3, 'Home');

    // --- v0.2.7: Sarah Domme route signal ---
    // Branches off Acceptance (sarah_arc == 1). The signal counter rises with
    // bedroom choices (pegging, cage-key, outfit). At signal >= 4 a Saturday
    // offer appears. Free content (story branch), no Patreon gate on the hint.
    if (iv('sarah_arc') === 1 && iv('sarah_dom_signal') >= 4 && !ib('sarah_dom_offer_done'))
        h(L.get('hint.sarah.dom_offer') || 'Sarah has been suggesting things. Accept on the next Saturday', false, 2, 'Main Story');

    // --- v0.2.7 → free in v0.2.9: Master Wellness Program (MR 9 post-intimate) ---
    // The isPat() gate was dropped in 0.2.9 when the Wellness scene went free —
    // free players who hit MR 9 post-intimate now get the +max-Energy signal too.
    if (mIntensity >= 1 && mr >= 9 && ib('master_intimate_done') && !ib('master_wellness_unlocked'))
        h(L.get('hint.master.wellness') || `{MasterName} has something to discuss with you after the last scene`, false, 2, 'Master');

    // --- v0.2.9: Owner's Property hardship — foreshadow the forced-cage event.
    // A player who took the opt-in hardship gets a run-up warning before the
    // Owner locks the cage on them himself (Fem 75 / Sub 75 / Owner route).
    if (ib('hardships_enabled') && ib('perk_owners_property') && mIntensity >= 2
        && fem >= 70 && sub >= 70 && !ib('chastity_forced_offered') && !ib('wearing_chastity'))
        h(L.get('hint.hardship.owners_property') || `{MasterName} has noticed how far you've come. He won't wait for you to ask for the cage.`, false, 2, 'Master');

    // --- v0.2.7: Roommate-route permanent-lock approach (now fires) ---
    // 40-locked-day milestone (lowered from 60 in 0.2.8.1.3). Reads
    // chastity_total_days_locked to match the gate, 5-day run-up window.
    const cdwR = iv('chastity_total_days_locked');
    if (cdwR >= 35 && cdwR < 40 && mIntensity === 1 && ib('wearing_chastity') && !ib('Permanent_Lock_Offer_seen'))
        h((L.get('hint.chastity.roommate_60') || 'Chastity {days}/40. Your roommate will say something soon.').replace('{days}', cdwR), false, 2, 'Master');

    // Fallback
    if (hints.length === 0) {
        h(L.get('hint.fallback.explore'), false, 4, 'Main Story');
        h(L.get('hint.fallback.check_phone'), false, 4, 'Phone');
    }

    // Sort + limit
    hints.sort((a, b) => {
        if (a.priority !== b.priority) return a.priority - b.priority;
        if (a.cat === 'Main Story' && b.cat !== 'Main Story') return -1;
        if (b.cat === 'Main Story' && a.cat !== 'Main Story') return 1;
        return (a.cat || '').localeCompare(b.cat || '');
    });

    // Filter dismissed hints. Keep dismissed-but-high-priority (0 = Master
    // orders) regardless — players shouldn't be able to hide critical
    // gameplay guidance, only "nice to have" suggestions.
    const dismissed = _loadDismissed();
    const visible = hints.filter(h => h.priority === 0 || !_isDismissed(h.text, dismissed));
    const display = visible.slice(0, 12);

    // Auto-collapse on first render if the list is long (player can expand
    // by clicking the caret). Tracked once per session via module flag so
    // we don't re-fold on every stat tick.
    const panel = document.getElementById('hints-panel');
    if (panel && !_hintsAutoCollapsedOnce && display.length > COLLAPSE_THRESHOLD) {
        list.classList.add('hidden');
        const svg = document.querySelector('#btn-hints-toggle svg');
        if (svg) svg.classList.add('rotated');
        _hintsAutoCollapsedOnce = true;
    }

    list.innerHTML = '';

    // Subtitle: click to open journal
    if (display.length > 0) {
        const subtitle = document.createElement('div');
        subtitle.className = 'hints-subtitle';
        subtitle.textContent = L.get('ui.click_details');
        list.appendChild(subtitle);
    }

    for (const hint of display) {
        const el = document.createElement('div');
        el.className = 'hint-item hint-clickable';

        const textEl = document.createElement('span');
        textEl.className = 'hint-text';
        textEl.textContent = hint.text;
        el.appendChild(textEl);

        // Dismiss button — only for non-critical (priority > 0) hints. Master
        // orders + rent countdowns can't be dismissed so players don't lock
        // themselves out of the progression signal.
        if (hint.priority > 0) {
            const xBtn = document.createElement('button');
            xBtn.className = 'hint-dismiss';
            xBtn.textContent = '\u00d7';
            xBtn.title = L.get('hint.dismiss') !== 'hint.dismiss' ? L.get('hint.dismiss') : 'Dismiss this hint';
            xBtn.setAttribute('aria-label', xBtn.title);
            xBtn.addEventListener('click', (e) => {
                e.stopPropagation();
                _dismissHint(hint.text);
                el.remove();
                // Update badge count
                if (badge) {
                    const remaining = list.querySelectorAll('.hint-item').length;
                    badge.textContent = remaining;
                    badge.classList.toggle('hidden', remaining === 0);
                }
            });
            el.appendChild(xBtn);
        }

        textEl.addEventListener('click', () => {
            // Open Journal overlay on Hints tab
            const journalOverlay = document.getElementById('overlay-journal');
            if (journalOverlay) {
                journalOverlay.classList.remove('hidden');
                events.emit(Events.OVERLAY_OPEN, 'overlay-journal');
                const tabs = journalOverlay.querySelectorAll('.journal-tab');
                const contents = journalOverlay.querySelectorAll('.journal-tab-content');
                tabs.forEach((t, i) => {
                    t.classList.toggle('active', i === 0);
                    if (contents[i]) contents[i].classList.toggle('hidden', i !== 0);
                });
                const journalContent = document.getElementById('journal-content');
                if (journalContent) {
                    renderJournalTab('hints', journalContent);
                }
            }
        });
        list.appendChild(el);
    }

    if (badge) {
        badge.textContent = display.length;
        badge.classList.toggle('hidden', display.length === 0);
    }
}
